import { Link } from '@tanstack/react-router';
import { InfoIcon, TriangleAlertIcon } from 'lucide-react';

import type { UnreadableWorkflow } from '@/features/tasks';
import {
  CompositionBar,
  type CompositionSegment,
} from '@/shared/components/CompositionBar';
import type {
  RestKey,
  RestOfWorkspace,
  RestPart,
} from '@/features/workspace-overview/restOfWorkspace';
import { SECTION_HEADING } from '@/shared/components/sectionHeadingClass';
import { WAITING_ON_SUBTASKS_FILL } from '@/shared/components/stateFills';
import { READER_WAITING_LABEL } from '@/features/tasks/statusPresentation';
import { useCanWork } from '@/features/workspaces/canWork';

export interface WorkspaceCountersProps {
  workspaceId: string;
  /** Every task once, by what it waits on (spec 153 US3). */
  rest: RestOfWorkspace;
  workflowTotal: number;
  publishedTotal: number;
  draftTotal: number;
}

function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/**
 * The rest of the workspace: every task once, as one bar, each part a link to
 * the Tasks page list that holds it (spec 153 US3).
 *
 * The four tiles this replaced printed "needs attention" and "waiting on you"
 * a second time beside Your turn, then a third time in the legend under them
 * (SC-002). Your turn's tasks are now one part, "with you", and no figure here
 * repeats a figure there.
 *
 * Every row is a link (FR-011), and each opens a list of exactly its count
 * (FR-018): the totals used to be text because a total spanning many boards
 * had no single destination, and the Tasks page is that destination.
 */
export function WorkspaceCounters({
  workspaceId,
  rest,
  workflowTotal,
  publishedTotal,
  draftTotal,
}: WorkspaceCountersProps) {
  const canWork = useCanWork();
  const segments: CompositionSegment[] = rest.parts.map((part) => ({
    key: part.key,
    label: partLabel(part.key, canWork),
    fill: PART[part.key].fill,
    value: part.value,
    link: (content) => (
      <Link
        to="/workspaces/$workspaceId/tasks"
        params={{ workspaceId }}
        search={{ state: part.states.join(',') }}
        className="hover:text-foreground focus-visible:ring-ring -mx-1 flex items-center gap-1.5 rounded-sm px-1 underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none pointer-coarse:min-h-11"
      >
        {content}
      </Link>
    ),
  }));

  return (
    <section className="flex flex-col gap-3">
      <h2 id="overview-totals" className={SECTION_HEADING}>
        Workspace totals
      </h2>

      {/*
        The panel keeps `role="group"`, named by the heading above it rather
        than by a duplicate `aria-label`.
      */}
      <div
        role="group"
        aria-labelledby="overview-totals"
        className="border-border bg-card flex flex-col gap-3 rounded-lg border px-4 py-3.5"
      >
        <CompositionBar
          segments={segments}
          total={rest.total}
          description={describe(rest.parts, rest.total, canWork)}
        />

        {/*
          Context: without it a workspace holding forty resting tasks reads
          like an empty one. Two lines, because they are two facts.
        */}
        <div className="text-muted-foreground border-border border-t pt-2.5 text-xs leading-relaxed tabular-nums">
          <p>{plural(rest.total, 'task', 'tasks')}</p>
          <p>
            {plural(workflowTotal, 'workflow', 'workflows')}
            {', '}
            {String(publishedTotal)} published,{' '}
            {draftTotal > 0 ? (
              <Link
                to="/workspaces/$workspaceId/workflows"
                params={{ workspaceId }}
                search={{ status: 'drafts' as const }}
                className="underline underline-offset-4"
              >
                {plural(draftTotal, 'draft', 'drafts')}
              </Link>
            ) : (
              plural(draftTotal, 'draft', 'drafts')
            )}
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * Each part's words and fill.
 *
 * With you and running are never adjacent: brand blue and brand purple are
 * hard to tell apart under protanopia, and neither may be re-stepped
 * (docs/brand.md FD-12). Waiting on subtasks sits between them in stripes of
 * the waiting colour, told from "with you" by pattern rather than by a lighter
 * fill, which failed 3:1 and matched "with you" in dark mode (FR-012).
 */
const PART: Record<RestKey, { label: string; fill: string }> = {
  'with-you': { label: 'with you', fill: 'bg-board-state-waiting' },
  subtasks: { label: 'waiting on subtasks', fill: WAITING_ON_SUBTASKS_FILL },
  running: { label: 'running', fill: 'bg-board-state-running' },
  'at-rest': { label: 'at rest', fill: 'bg-muted-foreground/25' },
};

/**
 * A part's words for the person reading them. Your turn's tasks are "with you"
 * to a member; a reader can act on none of them, so for them they wait on a
 * member (spec 193).
 */
function partLabel(key: RestKey, canWork: boolean): string {
  return key === 'with-you' && !canWork
    ? READER_WAITING_LABEL.toLowerCase()
    : PART[key].label;
}

/** The bar as a sentence, for anyone who cannot see it. */
function describe(
  parts: readonly RestPart[],
  total: number,
  canWork: boolean,
): string {
  const words = parts.map(
    (part) => `${String(part.value)} ${partLabel(part.key, canWork)}`,
  );
  return `Of ${plural(total, 'task', 'tasks')}: ${words.join(', ')}.`;
}

/**
 * Workflows whose boards could not be built, and the warning that their tasks
 * are therefore absent from every figure above.
 *
 * Saying so is the whole point. A workflow whose structure cannot be read
 * cannot be said to hold healthy tasks, and quietly counting them as such
 * would be the most damaging thing this page could do — a counter reading zero
 * because nothing could be read looks exactly like a counter reading zero
 * because nothing is wrong.
 */
export function UnreadableWorkflowsNotice({
  workspaceId,
  workflows,
}: {
  workspaceId: string;
  workflows: UnreadableWorkflow[];
}) {
  if (workflows.length === 0) return null;
  // A draft that has no steps yet is work in progress, not a fault (spec 127
  // FR-008): said as information, without the warning sign.
  const allDrafts = workflows.every((workflow) => workflow.isDraft);

  return (
    <div className="border-border flex flex-col gap-2 rounded-lg border border-dashed p-4">
      <p className="text-muted-foreground flex items-center gap-2 text-xs">
        {allDrafts ? (
          <InfoIcon aria-hidden="true" className="size-4" strokeWidth={1.5} />
        ) : (
          <TriangleAlertIcon
            aria-hidden="true"
            className="size-4"
            strokeWidth={1.5}
          />
        )}
        {allDrafts ? (
          workflows.length === 1 ? (
            '1 draft workflow is not on a board yet.'
          ) : (
            `${String(workflows.length)} draft workflows are not on a board yet.`
          )
        ) : (
          <>
            No board can be built for{' '}
            {workflows.length === 1 ? 'this workflow' : 'these workflows'}, so
            nothing they hold is counted anywhere on this page.
          </>
        )}
      </p>
      <ul className="flex flex-col gap-1">
        {workflows.map((workflow) => (
          <li key={workflow.workflowKey} className="text-xs">
            <span className="text-foreground">{workflow.workflowName}</span>{' '}
            <Link
              to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
              params={{ workspaceId, workflowKey: workflow.workflowKey }}
              aria-label={`Open ${workflow.workflowName} in the builder`}
              className="text-muted-foreground underline underline-offset-4"
            >
              Open in the builder
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
